import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import { colors } from '../theme/colors';
import { FiAlertTriangle, FiLock } from 'react-icons/fi';
import { ROUTE_TO_MODULE, getRoutePermission } from '../config/moduleRegistry';
import { reportForRoute } from '../utils/reportRights';

// Suspended company: data is kept for 7 days from the day it was suspended
// (the date Super Admin saved with the suspension). Shown as e.g. "24 October 2026".
const DATA_KEEP_DAYS = 7;
const dataKeptUntil = (company) => {
  const at = company && company.statusChangedAt ? new Date(company.statusChangedAt) : null;
  if (!at || Number.isNaN(at.getTime())) return '';
  const until = new Date(at.getTime() + DATA_KEEP_DAYS * 24 * 60 * 60 * 1000);
  return until.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ProtectedRoute = ({ children }) => {
  const { currentUser, activeCompany, companyUser, isModuleEnabled, hasPermission, canOpenReport, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
        <LoadingText>Loading...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Check if company is suspended
  if (activeCompany?.status === 'suspended') {
    const keptUntil = dataKeptUntil(activeCompany);
    return (
      <SuspendedContainer>
        <SuspendedIcon><FiAlertTriangle /></SuspendedIcon>
        <SuspendedTitle>Account Suspended</SuspendedTitle>
        <SuspendedText>
          Your company’s AmalERP account has been suspended due to an unpaid subscription
          (hosting) or an expired trial period.
        </SuspendedText>
        <DataNotice>
          Your data is safe and will be kept for <strong>7 days</strong>
          {keptUntil ? <> — until <strong>{keptUntil}</strong>.</> : ' from the date of suspension.'}
          <br />
          Clear the payment before then to restore full access — everything continues from where you left.
        </DataNotice>
        <SuspendedText>
          To renew your subscription or for any query, please contact us:
        </SuspendedText>
        <ContactBlock>
          <ContactRow>
            <ContactLabel>WhatsApp / Call</ContactLabel>
            <ContactNumber href="https://wa.me/923353058349" target="_blank" rel="noopener noreferrer">0335-3058349</ContactNumber>
          </ContactRow>
          <ContactRow>
            <ContactLabel>Email</ContactLabel>
            <ContactEmail href="mailto:info.switchertechno@gmail.com">info.switchertechno@gmail.com</ContactEmail>
          </ContactRow>
        </ContactBlock>
        <SuspendedCompany>{activeCompany.companyName}</SuspendedCompany>
      </SuspendedContainer>
    );
  }

  // Check if the route's module is enabled for this company
  const routeModule = ROUTE_TO_MODULE[location.pathname];
  if (routeModule && !isModuleEnabled(routeModule.id)) {
    return (
      <SuspendedContainer>
        <ModuleLockedIcon><FiLock /></ModuleLockedIcon>
        <SuspendedTitle>Module Not Available</SuspendedTitle>
        <SuspendedText>
          The <strong>{routeModule.name}</strong> module is not enabled for your company.
          Please contact your administrator for access.
        </SuspendedText>
        <GoToDashboardButton onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </GoToDashboardButton>
      </SuspendedContainer>
    );
  }

  // Fine-grained permission check: even if the module is enabled for the company,
  // the individual user's role may not permit viewing. Company admins and company
  // owners bypass this check via hasPermission() internals.
  const perm = getRoutePermission(location.pathname);
  // Reports: the head tick AND this single report's tick (Users & Roles → Reports ▸).
  const report = perm && perm.category === 'reports' ? reportForRoute(location.pathname) : null;
  if (perm && companyUser && (!hasPermission(perm.category, perm.key, 'view')
      || (report && canOpenReport && !canOpenReport(report.id)))) {
    const label = routeModule?.name || 'this page';
    return (
      <SuspendedContainer>
        <ModuleLockedIcon><FiLock /></ModuleLockedIcon>
        <SuspendedTitle>Access Denied</SuspendedTitle>
        <SuspendedText>
          You do not have permission to view <strong>{label}</strong>.
          Please contact your company administrator if you need access.
        </SuspendedText>
        <GoToDashboardButton onClick={() => navigate('/dashboard')}>
          Go to Dashboard
        </GoToDashboardButton>
      </SuspendedContainer>
    );
  }

  return children;
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${colors.background.default};
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid ${colors.border.light};
  border-top-color: ${colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin-top: 16px;
  color: ${colors.text.secondary};
  font-size: 16px;
`;

const SuspendedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${colors.background.default};
  padding: 24px;
  text-align: center;
`;

const SuspendedIcon = styled.div`
  font-size: 64px;
  color: ${colors.error.main};
  margin-bottom: 24px;
`;

const SuspendedTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const SuspendedText = styled.p`
  font-size: 16px;
  color: ${colors.text.secondary};
  max-width: 480px;
  line-height: 1.6;
  margin: 0 0 24px 0;
  em { color: ${colors.text.secondary}; }
`;

const ContactNumber = styled.a`
  display: inline-block;
  font-weight: 700;
  font-size: 18px;
  color: #25D366;
  text-decoration: none;
  font-family: monospace;
  letter-spacing: 0.5px;
  &:hover { text-decoration: underline; }
`;

const DataNotice = styled.div`
  max-width: 480px;
  margin: 0 0 24px 0;
  padding: 14px 18px;
  background: ${colors.warning.main}14;
  border: 1px solid ${colors.warning.main}55;
  border-radius: 8px;
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.text.primary};
`;

const ContactBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 28px 0;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ContactLabel = styled.span`
  font-size: 14px;
  color: ${colors.text.secondary};
`;

const ContactEmail = styled.a`
  font-weight: 600;
  font-size: 16px;
  color: ${colors.primary.main};
  text-decoration: none;
  word-break: break-all;
  &:hover { text-decoration: underline; }
`;

const SuspendedCompany = styled.div`
  padding: 12px 24px;
  background: ${colors.error.main}15;
  color: ${colors.error.main};
  border-radius: 8px;
  font-weight: 500;
`;

const ModuleLockedIcon = styled.div`
  font-size: 64px;
  color: ${colors.warning.main};
  margin-bottom: 24px;
`;

const GoToDashboardButton = styled.button`
  margin-top: 8px;
  padding: 12px 32px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

export default ProtectedRoute;
